import { useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";
import * as yup from "yup";
import { TextField } from "@mui/material";
import { mdiAccountCog, mdiImage } from "@mdi/js";
import { v4 as uuidv4 } from "uuid";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import AuthForm from "components/AuthForm";
import AvatarPicker from "components/AvatarPicker";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";
import generateTag from "utils/generateTag";
import resizeAvatarImage from "utils/resizeAvatarImage";
import ProfileApi from "api/profile";
import AuthApi from "api/auth";
import StorageApi, { StorageBuckets } from "api/storage";
import useBoundStore from "store";
import styles from "./setupAccount.module.scss";

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Name is required")
    .min(2, "Name can't be shorter than 2 characters")
    .max(30, "Name can't be longer than 30 characters")
    .matches(/^[a-zA-Z-â-ț-ș\s]*$/, "Name can only contain letters and spaces"),
});

function SetupAccount() {
  const [profileImage, setProfileImage] = useState<Blob | null>(null);
  const { auth, session, setUser } = useBoundStore(
    useShallow((state) => ({
      auth: state.user.auth,
      session: state.user.session,
      setUser: state.user.set,
    }))
  );
  const { showNotification } = useNotifier();
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const authApi = useMemo(() => new AuthApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(showNotification), [showNotification]);

  /**
   * Create profile for user and add a boolean into user metadata to remember
   * that the user already created a profile
   *
   * @param data
   */
  async function handleSetupAccount(data: { name: string }): Promise<void> {
    try {
      // const profileId = await refreshUntilProfileIdPresent();
      const lowerCaseName = data.name.toLowerCase();

      // Get users with same name
      const { tags, error: getUsersProfilesError } = await profileApi.getTagsByName(lowerCaseName);

      if (getUsersProfilesError) {
        throw new Error("Unable to get users profiles.");
      }

      // Create the profile of the user
      const tag = generateTag(lowerCaseName, tags);

      if (!tag) {
        throw new Error("Unable to generate tag.");
      }

      let imageName;
      if (profileImage) {
        imageName = `${uuidv4()}.jpg`;
      }

      const { error: createProfileError } = await profileApi.setupUserProfile({
        name: data.name,
        lower_case_name: lowerCaseName,
        tag,
        avatar: imageName,
        uuid: session!.user.id,
      });

      if (createProfileError) {
        throw new Error("Unable to create user profile.");
      }

      if (imageName && profileImage) {
        const { bigAvatarImg, smallAvatarImg } = await resizeAvatarImage(profileImage);

        // Save both big and small profile image to storage
        await storageApi.saveImage(bigAvatarImg, imageName, StorageBuckets.PROFILES_AVATARS);
        await storageApi.saveImage(smallAvatarImg, `s-${imageName}`, StorageBuckets.PROFILES_AVATARS);
      }

      // Add boolean to user metadata to remember that the user profile was created
      const { error: updateUserError } = await authApi.addProfileCreatedMeta();

      if (updateUserError) {
        throw updateUserError;
      }

      if (auth) {
        setUser({ auth: { ...auth, profileCreated: true } });
      }
    } catch (error) {
      showNotification({
        message: "Unable to create user profile.",
        severity: "error",
      });
    }
  }

  /**
   * Set profile image
   *
   * @param image
   */
  function handleSaveProfileImage(image: Blob): void {
    setProfileImage(image);
  }

  /**
   * Unset profile image
   */
  function handleDeleteProfileImage(): void {
    setProfileImage(null);
  }

  return (
    <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
      <AuthForm<SetupAccountForm>
        avatarIcon={mdiAccountCog}
        notInputChildren={[0]}
        onSubmit={handleSetupAccount}
        submitLabel='Setup account'
        title='Setup account'
        validationSchema={validationSchema}
      >
        <AvatarPicker
          id='setup-account-avatar'
          className={styles.avatarPicker}
          img={profileImage ? URL.createObjectURL(profileImage) : undefined}
          onDeleteImage={handleDeleteProfileImage}
          onSelectImage={handleSaveProfileImage}
          placeholder={<Icon path={mdiImage} title='Profile image placeholder' size={1.5} />}
        />
        <TextField
          name='name'
          label='Name'
          placeholder='This name will be seen by other users'
          autoComplete='name'
          fullWidth
          autoFocus
        />
      </AuthForm>
    </PageLayout>
  );
}

export default SetupAccount;

interface SetupAccountForm extends Record<string, unknown> {
  name: string;
}
